import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { siteConfig } from '@/constants/site'

const organization = {
    '@type': 'Organization',
    '@id': `${siteConfig.url}/#organization`,
    name: siteConfig.name,
    url: `${siteConfig.url}/`,
    email: siteConfig.email,
    description: 'The web + product agency for teams who value speed + quality above all else.',
    logo: { '@type': 'ImageObject', url: `${siteConfig.url}/images/native-logo.svg` },
    sameAs: [siteConfig.links.twitter, siteConfig.links.github]
}

const website = {
    '@type': 'WebSite',
    '@id': `${siteConfig.url}/#website`,
    url: `${siteConfig.url}/`,
    name: siteConfig.name,
    publisher: { '@id': `${siteConfig.url}/#organization` },
    inLanguage: 'en'
}

export const pageUrl = (path: string): string => `${siteConfig.url}${path === '/' ? '/' : path}`

// Titles are kept verbatim from the migrated site, so the page passes its full document title.
export const Seo: FC<SeoProps> = ({
    title,
    description,
    path,
    ogTitle,
    ogImage = siteConfig.ogImage,
    noindex,
    jsonLd = []
}): ReactNode => {
    const url = pageUrl(path)
    const socialTitle = ogTitle ?? title
    const graph = { '@context': 'https://schema.org', '@graph': [organization, website, ...jsonLd] }

    return (
        <Head>
            <title>{title}</title>

            <meta name='description' content={description} />
            {noindex && <meta name='robots' content='noindex, nofollow' />}
            <link rel='canonical' href={url} />
            <link rel='author' href={`${siteConfig.url}/`} />

            <meta property='og:type' content='website' />
            <meta property='og:site_name' content={siteConfig.name} />
            <meta property='og:locale' content='en_US' />
            <meta property='og:title' content={socialTitle} />
            <meta property='og:description' content={description} />
            <meta property='og:url' content={url} />
            <meta property='og:image' content={ogImage} />
            <meta property='og:image:width' content='1200' />
            <meta property='og:image:height' content='630' />
            <meta property='og:image:alt' content={socialTitle} />

            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:site' content='@thenativeagency' />
            <meta name='twitter:creator' content='@thenativeagency' />
            <meta name='twitter:title' content={socialTitle} />
            <meta name='twitter:description' content={description} />
            <meta name='twitter:image' content={ogImage} />

            <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(graph) }} />
        </Head>
    )
}
